import { useState, type ReactElement } from 'react';
import { useSession } from '../../auth';
import { apiFetch, ApiError } from '../../auth/api-client';
import { trackFailed } from '../../../../../shared-web/track';
import { Modal } from './Modal';
import { CLIENT_COPY } from '../../../../../shared/client-copy.js';
import './LegalUpdateNotice.css';

/**
 * NOT MOUNTED (2026-10-02 拍板, jam). Since 2026-10-15 an update is told by a
 * text and by the horn, both opening /legal/update (pages/LegalUpdate.tsx);
 * `effectiveDateLabel` below is shared with that page. EmberApp does not
 * render this dialog.
 *
 * The update notice (2026-10-01 拍板, jam): when the Terms and the Privacy
 * Policy in force are newer than the ones this account signed, the person is
 * told once — what changed, the date it takes effect, the two documents to
 * review — and `Continue to Ember` is the agreement. Not a wall: nothing is
 * refused while the dialog stands (that is the under-the-floor door in
 * EmberApp), and there is no other way to put the dialog away, so the one
 * tap is also the record (POST /api/legal/accept mode=notice stamps the
 * version in force).
 *
 * Owed by /api/me `legal.notice_due`; a refused write closes the dialog for
 * this visit and leaves it owed, so the next load asks again rather than
 * holding the person on a dialog that cannot be answered.
 *
 * Its sentence is src/shared/client-copy.js legal.legal_update (2026-10-21
 * Riley), `{date}` filled with effectiveDateLabel.
 */

/** The dialog's own once-per-visit memory for a write that did not land — a page reload asks again. */
const DISMISSED_KEY = 'ember.legal_notice.dismissed.v1';

function readDismissed(version: string): boolean {
  try { return sessionStorage.getItem(DISMISSED_KEY) === version; } catch { return false; }
}
function writeDismissed(version: string): void {
  try { sessionStorage.setItem(DISMISSED_KEY, version); } catch { /* storage blocked — the dialog simply asks again */ }
}

/** `2026-10-02` → `October 2, 2026`; a version that is not a date is shown as it is. */
export function effectiveDateLabel(version: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(version);
  if (!m) return version;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export function LegalUpdateNotice(): ReactElement | null {
  const { status, session, refresh } = useSession();
  const version = session?.legal_current_version || '';
  const [closed, setClosed] = useState(false);
  const [busy, setBusy] = useState(false);

  if (status !== 'authenticated' || !session?.legal_notice_due || session.deletion_pending || session.account_closed) return null;
  if (closed || !version || readDismissed(version)) return null;

  const onContinue = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    try {
      await apiFetch('/api/legal/accept', { method: 'POST', body: { version, mode: 'notice' } });
      setClosed(true);
      // The session's word, not a local flip: /api/me now says the account is current.
      void refresh();
    } catch (err) {
      const reason = err instanceof ApiError
        ? String((err.body as { error?: unknown } | null)?.error || `http_${err.status}`)
        : 'network_error';
      trackFailed('legal_notice_accept', reason);
      writeDismissed(version);
      setClosed(true);
    }
  };

  return (
    <Modal onClose={() => {}} labelledBy="legal-notice-title" className="legal-notice" dismissible={false} focusDialog sheet="phone">
      <div data-log-seen="legal_notice" data-log-state={version}>
        <h2 id="legal-notice-title" className="legal-notice-title">We’ve updated our Terms &amp; Privacy Policy</h2>
        <p className="legal-notice-body">
          {CLIENT_COPY.legal.legal_update.body.replace('{date}', effectiveDateLabel(version))}
        </p>
        <p className="legal-notice-links">
          <a href="/terms" target="_blank" rel="noopener noreferrer" data-log="legal_notice_terms">Review Terms</a>
          <span aria-hidden> · </span>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" data-log="legal_notice_privacy">Review Privacy Policy</a>
        </p>
        <div className="legal-notice-actions">
          <button
            type="button"
            className="ember-btn ember-btn-primary"
            data-log="legal_notice_continue"
            disabled={busy}
            onClick={() => { void onContinue(); }}
          >
            Continue to Ember
          </button>
        </div>
      </div>
    </Modal>
  );
}
